import {
  Component,
  DestroyRef,
  inject,
  input,
  signal,
  computed,
  ChangeDetectionStrategy,
  OnInit,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { DialogRef } from '@angular/cdk/dialog';
import { CostComponent } from '@core/components/cost/cost.component';
import { SvgIconComponent } from 'angular-svg-icon';
import { NgParticlesService, NgxParticlesModule } from '@tsparticles/angular';
import { Engine, ISourceOptions } from '@tsparticles/engine';
import { loadSlim } from '@tsparticles/slim';
import { SkinsListComponent } from '@core/components/skins-list/skins-list.component';
import { CheckboxComponent } from '@core/components/checkbox/checkbox.component';
import { Skin } from '@core/interfaces/skin.interface';
import {
  WIN_MODAL_COUNTDOWN_SECONDS,
  WinModalAction,
} from '@core/constants/win-modal.constants';
import { ToastrService } from 'ngx-toastr';
import { WIN_MODAL_GAME_CONFIG, WinModalButton } from './win-modal.config';
import { WinModalPort } from './win-modal.port';
import { WinModalInput } from './win-modal.service';
import 'number-flow';

@Component({
  selector: 'app-win-modal',
  templateUrl: './win-modal.component.html',
  styleUrls: ['./win-modal.component.scss'],
  standalone: true,
  imports: [
    CostComponent,
    SvgIconComponent,
    NgxParticlesModule,
    SkinsListComponent,
    CheckboxComponent,
    DecimalPipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  host: {
    class: 'modal-container flex flex-col',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WinModalComponent implements OnInit {
  private readonly port = inject(WinModalPort);
  private readonly toastr = inject(ToastrService);
  private readonly dialogRef = inject(DialogRef);
  private ngParticlesService = inject(NgParticlesService);
  private destroyRef = inject(DestroyRef);

  readonly win = input.required<WinModalInput>();

  readonly skins = computed(() => this.win().skins);
  readonly totalValue = computed(() => this.win().totalValue);
  readonly winnerAvatar = computed(() => this.win().winnerAvatar);
  readonly winnerChance = computed(() => this.win().winnerChance);
  readonly config = computed(() => WIN_MODAL_GAME_CONFIG[this.win().game]);

  readonly selectedSkins = signal<Skin[]>([]);
  readonly selectAll = signal(true);
  readonly countdown = signal(WIN_MODAL_COUNTDOWN_SECONDS);
  readonly timerRunning = signal(true);
  readonly busy = signal<WinModalAction | null>(null);
  private timerInterval: ReturnType<typeof setInterval> | undefined;
  private destroyed = false;

  readonly selectable = computed(() => this.config().selectable);
  readonly showFirstButton = computed(() =>
    !(this.win().game === 'upgrader' && !!this.win().bonusWager)
  );
  readonly visibleButtons = computed(() =>
    this.showFirstButton() ? [this.config().first, this.config().auto] : [this.config().auto]
  );

  readonly selectedCount = computed(() => this.selectedSkins().length);
  readonly totalCount = computed(() => this.skins().length);

  readonly selectedValue = computed(() =>
    this.selectedSkins().reduce((acc, skin) => acc + skin.price, 0)
  );

  particlesOptions: ISourceOptions = {
    fullScreen: { enable: false },
    particles: {
      number: { value: 30 },
      color: { value: ['#FFD700', '#FFA500', '#ffffff'] },
      move: {
        direction: 'none',
        enable: true,
        outModes: { default: 'out' },
        random: true,
        speed: 1,
        straight: false,
      },
      opacity: {
        value: { min: 0.17, max: 0.5 },
      },
      size: {
        value: { min: 1, max: 1.2 },
      },
    },
  };

  ngOnInit() {
    this.ngParticlesService.init(async (engine: Engine) => {
      await loadSlim(engine);
    });

    this.selectedSkins.set([...this.skins()]);

    this.timerInterval = setInterval(() => this.tick(), 1000);

    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      this.stopTimer();
    });
  }

  labelFor(btn: WinModalButton): string {
    return this.timerRunning()
      ? btn.label.replace('{n}', String(this.countdown()))
      : btn.idleLabel;
  }

  onSelectAllChange(checked: boolean) {
    this.selectAll.set(checked);
    if (checked) {
      this.selectedSkins.set([...this.skins()]);
    } else {
      this.selectedSkins.set([]);
    }
  }

  onClose() {
    this.dialogRef.close();
  }

  /** Implements plan §3 edge rules exactly. */
  async act(action: WinModalAction, source: 'manual' | 'auto') {
    if (this.busy() !== null) return;

    if (this.selectedCount() === 0) {
      // Manual is unreachable (buttons disabled with 0 selected).
      if (source === 'auto') {
        this.stopTimer();
        this.dialogRef.close();
      }
      return;
    }

    const r = this.port.resolveEvent(action, this.win());
    if ('error' in r) {
      if (source === 'manual') {
        this.toastr.error(r.error);
      } else {
        this.stopTimer();
        this.dialogRef.close();
      }
      return;
    }

    this.stopTimer();
    this.busy.set(action);
    const { ok } = await this.port.run(r.event, action, this.win(), this.selectedSkins());
    this.busy.set(null);

    // Replaced or closed while the ACK was in flight: never close another modal.
    if (this.destroyed) return;
    if (ok) this.dialogRef.close();
  }

  private tick() {
    if (this.busy() !== null || !this.timerRunning()) return;
    const next = this.countdown() - 1;
    this.countdown.set(next);
    if (next === 0) {
      void this.act(this.config().auto.action, 'auto');
    }
  }

  private stopTimer() {
    if (this.timerInterval !== undefined) {
      clearInterval(this.timerInterval);
      this.timerInterval = undefined;
    }
    this.timerRunning.set(false);
  }
}
